import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import Avatar from 'components/Avatar';
import CommentMentionTextarea from 'components/CommentMentionTextarea';
import API_ENDPOINTS from 'utils/api/endpoints';
import { useBudgetMutation } from 'utils/api/mutations';
import { useBudgetQueries } from 'utils/api/queries';
import { buildPrivateBudgetShareUrl } from 'utils/budgetDeepLinks';
import { formatCurrency } from 'utils/helper';
import { Icon } from 'utils/icons';

import AiAssistantTab from './AiAssistantTab';

const PERSON_NAME_PATTERN =
  /^[A-Z][A-Za-z.'’-]*(?:\s+[A-Z][A-Za-z.'’-]*){1,3}$/;

const normalizeRateRole = role => {
  let label = String(role || 'Rate context').trim();
  label = label.replace(/\s*\(s\)/gi, '').replace(/\s*\(unit\)\s*$/i, '');
  if (!label.includes(' - ')) return label;

  const separatorIndex = label.lastIndexOf(' - ');
  const suffix = label.slice(separatorIndex + 3).trim();
  return PERSON_NAME_PATTERN.test(suffix)
    ? label.slice(0, separatorIndex).trim()
    : label;
};

const getRateLabel = rate =>
  rate?.rate_label || rate?.union_status || 'Estimated';

export const getRateVerificationNotice = insights => {
  const hasJobRates = (insights || []).some(insight => {
    if (insight?.item_type) return insight.item_type === 'job';
    return /\b(?:non[- ]?union|union|iatse|sag-aftra|dga|wga|teamsters)\b/i.test(
      insight?.union_status || ''
    );
  });

  return hasJobRates
    ? 'AI estimate only. Verify current local and union rates.'
    : 'AI estimate only. Verify current local rates.';
};

const getInsightRates = (insight, fallbackLocation) =>
  Array.isArray(insight?.rates) && insight.rates.length
    ? insight.rates
    : [{ ...insight, location: insight?.location || fallbackLocation || '' }];

export const groupInsightsByRole = (insights, orderedLocations = []) => {
  const roles = new Map();
  const locationOrder = new Map(
    (orderedLocations || []).map((location, index) => [
      String(location).toLocaleLowerCase(),
      index,
    ])
  );
  const fallbackLocation = orderedLocations?.[0] || '';

  (insights || []).forEach(insight => {
    const role = normalizeRateRole(insight?.role);
    const roleGroup = roles.get(role) || {
      role,
      locations: new Map(),
      rationales: [],
    };
    getInsightRates(insight, fallbackLocation).forEach(rate => {
      const location = String(rate?.location || '').trim();
      const locationKey = location.toLocaleLowerCase();
      const locationGroup = roleGroup.locations.get(locationKey) || {
        location,
        classifications: new Map(),
      };
      const rateLabel = getRateLabel(rate);
      const classification = locationGroup.classifications.get(rateLabel) || {
        rateLabel,
        rates: [],
      };
      const duplicateEntry = classification.rates.some(
        entry =>
          entry.estimate_low === rate.estimate_low &&
          entry.estimate_high === rate.estimate_high &&
          String(entry.unit).toLocaleLowerCase() ===
            String(rate.unit).toLocaleLowerCase()
      );
      if (!duplicateEntry) classification.rates.push(rate);
      locationGroup.classifications.set(rateLabel, classification);
      roleGroup.locations.set(locationKey, locationGroup);
      if (
        insight?.rationale &&
        !roleGroup.rationales.includes(insight.rationale)
      ) {
        roleGroup.rationales.push(insight.rationale);
      }
    });
    roles.set(role, roleGroup);
  });

  return [...roles.values()].map(roleGroup => ({
    ...roleGroup,
    locations: [...roleGroup.locations.values()]
      .sort((left, right) => {
        const fallbackIndex = orderedLocations.length;
        return (
          (locationOrder.get(left.location.toLocaleLowerCase()) ??
            fallbackIndex) -
          (locationOrder.get(right.location.toLocaleLowerCase()) ??
            fallbackIndex)
        );
      })
      .map(locationGroup => ({
        ...locationGroup,
        classifications: [...locationGroup.classifications.values()],
      })),
  }));
};

const normalizeRateUnit = unit => {
  const normalized = String(unit || '')
    .trim()
    .toLocaleLowerCase();
  if (normalized === 'daily' || normalized === 'days') return 'day';
  if (normalized === 'weekly' || normalized === 'weeks') return 'week';
  return normalized;
};

export const getRateDisplayPair = rates => {
  const dayRate = (rates || []).find(
    rate => normalizeRateUnit(rate.unit) === 'day'
  );
  const weekRate = (rates || []).find(
    rate => normalizeRateUnit(rate.unit) === 'week'
  );

  return {
    primaryRate: dayRate || weekRate || rates?.[0] || null,
    weeklyEquivalent: dayRate && weekRate ? weekRate : null,
  };
};

const formatRateRange = (rate, currency) => {
  if (!rate) return '';
  const unit = normalizeRateUnit(rate.unit);
  return `${formatCurrency(rate.estimate_low, currency)}–${formatCurrency(
    rate.estimate_high,
    currency
  )}${unit ? `/${unit}` : ''}`;
};

export const mergeFocusedInsights = (currentInsights, nextInsights) => {
  const incomingRoleKeys = new Set(
    (nextInsights || []).map(insight =>
      normalizeRateRole(insight?.role).toLocaleLowerCase()
    )
  );

  return [
    ...(currentInsights || []).filter(
      insight =>
        !incomingRoleKeys.has(
          normalizeRateRole(insight?.role).toLocaleLowerCase()
        )
    ),
    ...(nextInsights || []),
  ];
};

const conciseText = (value, maxLength = 120) => {
  const normalized = String(value || '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!normalized) return '';

  const firstSentence = normalized.match(/^.*?[.!?](?:\s|$)/)?.[0]?.trim();
  const summary = firstSentence || normalized;

  return summary.length > maxLength
    ? `${summary.slice(0, maxLength - 1).trimEnd()}…`
    : summary;
};

const formatCommentTimestamp = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

const formatColumnName = columnKey =>
  String(columnKey || 'cell')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, character => character.toUpperCase());

const getCommentLocation = comment => {
  if (comment?.isBudgetWide) return 'Budget-wide comment';
  const hierarchy = comment?.view_state?.hierarchy || [];
  const path = hierarchy
    .map(item => item?.name)
    .filter(Boolean)
    .join(' › ');
  const column = formatColumnName(comment?.column_key);
  return path ? `${path} · ${column}` : column;
};

const getStoredPanelTab = budgetId => {
  if (typeof window === 'undefined' || !budgetId) return 'rate-card';
  const storedTab = window.localStorage?.getItem(
    `cinebudget-budget-side-panel-tab:${budgetId}`
  );
  return ['rate-card', 'comments', 'assistant'].includes(storedTab)
    ? storedTab
    : 'rate-card';
};

const getAvailablePanelTab = (budgetId, rateCardAvailable) => {
  const storedTab = getStoredPanelTab(budgetId);
  return storedTab === 'rate-card' && !rateCardAvailable
    ? 'comments'
    : storedTab;
};

const AiInsightsPanel = ({
  budgetId,
  categoryId,
  focusItems,
  onBudgetChange,
  onHide,
  open,
  rateCardAvailable,
  refreshKey,
  mentionableUsers,
  requestedTab,
}) => {
  const canShowRateCard = rateCardAvailable ?? Boolean(categoryId);
  const [activeTab, setActiveTab] = useState(() =>
    getAvailablePanelTab(budgetId, canShowRateCard)
  );
  const [budgetCommentDraft, setBudgetCommentDraft] = useState('');
  const [budgetCommentMentionIds, setBudgetCommentMentionIds] = useState([]);
  const budgetCommentComposerRef = useRef(null);
  const [state, setState] = useState({
    loading: false,
    data: null,
    error: '',
    contextKey: '',
  });
  const focusItemsKey = (focusItems || [])
    .map(item =>
      String(item || '')
        .replace(/\s+/g, ' ')
        .trim()
    )
    .filter(Boolean)
    .join('\u001f');
  const requestFocusItems = useMemo(
    () => (focusItemsKey ? focusItemsKey.split('\u001f') : []),
    [focusItemsKey]
  );
  const {
    data: commentsResponse,
    isError: hasCommentsError,
    isLoading: isCommentsLoading,
  } = useBudgetQueries.useBudgetCellCommentThreadQuery({
    budgetId,
    enabled: Boolean(open && budgetId && activeTab === 'comments'),
    config: {
      refetchInterval: 2000,
      refetchIntervalInBackground: true,
    },
  });
  const {
    data: budgetCommentsResponse,
    isError: hasBudgetCommentsError,
    isLoading: isBudgetCommentsLoading,
    refetch: refetchBudgetComments,
  } = useBudgetQueries.useBudgetCommentsQuery({
    budgetId,
    enabled: Boolean(open && budgetId && activeTab === 'comments'),
    config: {
      refetchInterval: 2000,
      refetchIntervalInBackground: true,
    },
  });
  const createBudgetCommentMutation =
    useBudgetMutation.useCreateBudgetCommentMutation();
  const markCommentNotificationsReadMutation =
    useBudgetMutation.useMarkCommentNotificationsReadMutation();
  const comments = useMemo(
    () =>
      [
        ...(budgetCommentsResponse?.data || []).map(comment => ({
          ...comment,
          isBudgetWide: true,
        })),
        ...(commentsResponse?.data || []),
      ].sort(
        (left, right) =>
          new Date(right.created_on).getTime() -
          new Date(left.created_on).getTime()
      ),
    [budgetCommentsResponse?.data, commentsResponse?.data]
  );
  const commentsReadKey = useMemo(
    () =>
      comments
        .map(
          comment =>
            `${comment.isBudgetWide ? 'b' : 'c'}:${comment.id}:${
              comment.updated_on || comment.created_on || ''
            }`
        )
        .join(','),
    [comments]
  );

  useEffect(() => {
    setActiveTab(getAvailablePanelTab(budgetId, canShowRateCard));
  }, [budgetId, canShowRateCard]);

  useEffect(() => {
    if (requestedTab === 'comments' || requestedTab === 'assistant') {
      setActiveTab(requestedTab);
    } else if (requestedTab === 'rate-card' && canShowRateCard) {
      setActiveTab(requestedTab);
    }
  }, [canShowRateCard, requestedTab]);

  useEffect(() => {
    if (typeof window === 'undefined' || !budgetId) return;
    window.localStorage?.setItem(
      `cinebudget-budget-side-panel-tab:${budgetId}`,
      activeTab
    );
  }, [activeTab, budgetId]);

  useEffect(() => {
    if (!open || activeTab !== 'comments' || !budgetId) return;
    markCommentNotificationsReadMutation.mutate({
      budget: budgetId,
      comment_scope: 'all',
    });
    // Opening this tab is the read boundary; polling should not retrigger it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, budgetId, commentsReadKey, open]);

  useEffect(() => {
    if (!open || activeTab !== 'rate-card' || !canShowRateCard) {
      return undefined;
    }

    if (!budgetId || !categoryId) {
      setState({ loading: false, data: null, error: '' });
      return undefined;
    }

    let active = true;
    const controller = new AbortController();
    const requestContextKey = `${budgetId}:${categoryId}`;
    const focusedItems = requestFocusItems;
    setState(previous => ({
      loading: true,
      data: previous.contextKey === requestContextKey ? previous.data : null,
      error: '',
      contextKey: requestContextKey,
    }));

    API_ENDPOINTS.ai_suggestions
      .insights(
        {
          budget_id: budgetId,
          category_id: categoryId,
          focus_items: focusedItems,
        },
        { signal: controller.signal }
      )
      .then(response => {
        if (active) {
          setState(previous => ({
            loading: false,
            data:
              focusedItems.length > 0 &&
              previous.contextKey === requestContextKey &&
              previous.data
                ? {
                    ...response.data,
                    insights: mergeFocusedInsights(
                      previous.data.insights,
                      response.data?.insights
                    ),
                  }
                : response.data,
            error: '',
            contextKey: requestContextKey,
          }));
        }
      })
      .catch(error => {
        if (!active || error?.code === 'ERR_CANCELED') return;

        setState(previous => ({
          loading: false,
          data:
            previous.contextKey === requestContextKey ? previous.data : null,
          error:
            error?.response?.data?.detail ||
            'AI rate estimates are unavailable right now.',
          contextKey: requestContextKey,
        }));
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [
    activeTab,
    budgetId,
    canShowRateCard,
    categoryId,
    open,
    refreshKey,
    requestFocusItems,
  ]);

  const assumptions = state.data?.assumptions || {};
  const insights = state.data?.insights || [];
  const insightsByRole = useMemo(
    () =>
      groupInsightsByRole(
        state.data?.insights || [],
        state.data?.assumptions?.rate_locations?.length
          ? state.data.assumptions.rate_locations
          : state.data?.assumptions?.shooting_locations || []
      ),
    [
      state.data?.assumptions?.rate_locations,
      state.data?.assumptions?.shooting_locations,
      state.data?.insights,
    ]
  );
  const currency = assumptions.currency;
  const handleOpenComment = comment => {
    const destination = buildPrivateBudgetShareUrl({
      appBaseUrl: window.location.origin,
      budgetId,
      viewState: comment?.view_state,
    });
    if (destination) window.location.href = destination;
  };
  const submitBudgetComment = async event => {
    event.preventDefault();
    const body = budgetCommentDraft.trim();
    if (!body || !budgetId || createBudgetCommentMutation.isLoading) return;
    const submittedMentionIds = [...budgetCommentMentionIds];

    setBudgetCommentDraft('');
    setBudgetCommentMentionIds([]);

    try {
      await createBudgetCommentMutation.mutateAsync({
        budget: budgetId,
        body,
        mentioned_user_ids: submittedMentionIds,
      });
      refetchBudgetComments();
    } catch (error) {
      setBudgetCommentDraft(currentDraft => currentDraft || body);
      setBudgetCommentMentionIds(currentMentionIds =>
        currentMentionIds.length ? currentMentionIds : submittedMentionIds
      );
      toast.error('Unable to add this budget comment. Please try again.');
    }
  };
  const handleBudgetCommentKeyDown = event => {
    if (event.key !== 'Enter' || !event.shiftKey) return;
    event.preventDefault();
    event.currentTarget.form?.requestSubmit();
  };
  const panelTitle = {
    'rate-card': 'AI Rate Card',
    comments: 'Comments',
    assistant: 'AI Assistant',
  }[activeTab];

  return (
    <aside
      className={`ai-insights ai-insights-${open ? 'open' : 'closed'}`}
      aria-hidden={!open}
      aria-label="Budget insights, comments, and AI Assistant"
    >
      <header className="ai-insights__header">
        <div className="ai-insights__title">
          {activeTab === 'rate-card' ? (
            <span aria-hidden="true">✦</span>
          ) : activeTab === 'comments' ? (
            <Icon aria-hidden="true" name="CommentIcon" />
          ) : (
            <span aria-hidden="true">✦</span>
          )}
          <h2>{panelTitle}</h2>
        </div>
        <button
          type="button"
          className="ai-insights__hide"
          aria-label="Hide budget side panel"
          title="Hide budget side panel"
          onClick={onHide}
          tabIndex={open ? 0 : -1}
        >
          ×
        </button>
      </header>
      <div
        className="ai-insights__tabs"
        role="tablist"
        aria-label="Budget side panel"
      >
        <button
          type="button"
          className={`ai-insights__tab ${
            activeTab === 'rate-card' ? 'is-active' : ''
          }`}
          role="tab"
          aria-selected={activeTab === 'rate-card'}
          aria-disabled={!canShowRateCard}
          disabled={!canShowRateCard}
          onClick={() => setActiveTab('rate-card')}
          tabIndex={open ? 0 : -1}
          title={
            canShowRateCard
              ? undefined
              : 'Open a detail view to use the AI Rate Card.'
          }
        >
          AI Rate Card
        </button>
        <button
          type="button"
          className={`ai-insights__tab ${
            activeTab === 'comments' ? 'is-active' : ''
          }`}
          role="tab"
          aria-selected={activeTab === 'comments'}
          onClick={() => setActiveTab('comments')}
          tabIndex={open ? 0 : -1}
        >
          Comments
        </button>
        <button
          type="button"
          className={`ai-insights__tab ${
            activeTab === 'assistant' ? 'is-active' : ''
          }`}
          role="tab"
          aria-selected={activeTab === 'assistant'}
          onClick={() => setActiveTab('assistant')}
          tabIndex={open ? 0 : -1}
        >
          AI Assistant
        </button>
      </div>
      {activeTab === 'rate-card' ? (
        <div className="ai-insights__content">
          {state.loading && (
            <p className="ai-insights__status">
              {state.data ? 'Updating rate context…' : 'Finding rate context…'}
            </p>
          )}
          {state.error && <p className="ai-insights__error">{state.error}</p>}
          {insightsByRole.map(group => (
            <section className="ai-insights__rate-group" key={group.role}>
              <h3>{group.role} rates:</h3>
              {group.locations.map(locationGroup => (
                <div
                  className="ai-insights__rate-location"
                  key={locationGroup.location || 'general'}
                >
                  {locationGroup.location && (
                    <p className="ai-insights__rate-location-name">
                      {locationGroup.location}
                    </p>
                  )}
                  <ul>
                    {locationGroup.classifications.map(classification => {
                      const { primaryRate, weeklyEquivalent } =
                        getRateDisplayPair(classification.rates);

                      return (
                        <li key={classification.rateLabel}>
                          <span>{classification.rateLabel}</span>
                          <strong>
                            {formatRateRange(primaryRate, currency)}
                          </strong>
                          {weeklyEquivalent && (
                            <span className="ai-insights__weekly-equivalent">
                              {' '}
                              ({formatRateRange(weeklyEquivalent, currency)})
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
              {group.rationales[0] && (
                <p className="ai-insights__takeaway">
                  {conciseText(group.rationales[0])}
                </p>
              )}
            </section>
          ))}
          {!state.loading && !state.error && insightsByRole.length === 0 && (
            <p className="ai-insights__status">
              No rate context was found for this detail view.
            </p>
          )}
          <p className="ai-insights__notice">
            {getRateVerificationNotice(insights)}
          </p>
        </div>
      ) : activeTab === 'comments' ? (
        <>
          <div className="ai-insights__comments" role="tabpanel">
            {(isCommentsLoading || isBudgetCommentsLoading) && (
              <p className="ai-insights__status">Loading comments…</p>
            )}
            {(hasCommentsError || hasBudgetCommentsError) && (
              <p className="ai-insights__error">
                Comments are unavailable right now.
              </p>
            )}
            {!isCommentsLoading &&
              !isBudgetCommentsLoading &&
              !hasCommentsError &&
              !hasBudgetCommentsError &&
              !comments.length && (
                <p className="ai-insights__status">
                  There are no comments on this budget yet.
                </p>
              )}
            {comments.map(comment => (
              <article className="ai-insights__comment" key={comment.id}>
                <Avatar
                  name={comment.author_name || comment.author_initials}
                  className="ai-insights__comment-avatar"
                  image={
                    comment.author_profile_image ? (
                      <img src={comment.author_profile_image} alt="" />
                    ) : null
                  }
                />
                <div className="ai-insights__comment-body">
                  <div className="ai-insights__comment-meta">
                    <strong>{comment.author_name || 'Collaborator'}</strong>
                    <time dateTime={comment.created_on}>
                      {formatCommentTimestamp(comment.created_on)}
                    </time>
                  </div>
                  <p>{comment.body}</p>
                  {!comment.isBudgetWide && comment.view_state && (
                    <button
                      type="button"
                      className="ai-insights__comment-open"
                      onClick={() => handleOpenComment(comment)}
                    >
                      Open comment <span aria-hidden="true">→</span>
                    </button>
                  )}
                  <span className="ai-insights__comment-location">
                    {getCommentLocation(comment)}
                  </span>
                </div>
              </article>
            ))}
          </div>
          <form
            className="ai-insights__budget-comment-composer"
            onSubmit={submitBudgetComment}
          >
            <CommentMentionTextarea
              ariaLabel="Add a budget-wide comment"
              disabled={createBudgetCommentMutation.isLoading}
              inputRef={budgetCommentComposerRef}
              maxLength={2000}
              mentionableUsers={mentionableUsers}
              mentionedUserIds={budgetCommentMentionIds}
              onChange={setBudgetCommentDraft}
              onKeyDown={handleBudgetCommentKeyDown}
              onMentionedUserIdsChange={setBudgetCommentMentionIds}
              placeholder="Add a budget-wide comment"
              value={budgetCommentDraft}
            />
            <button
              aria-label="Send budget-wide comment"
              disabled={
                !budgetCommentDraft.trim() ||
                createBudgetCommentMutation.isLoading
              }
              type="submit"
            >
              <Icon aria-hidden="true" name="ArrowUpIcon" />
            </button>
          </form>
        </>
      ) : (
        open && (
          <AiAssistantTab
            budgetId={budgetId}
            categoryId={categoryId}
            onBudgetChange={onBudgetChange}
          />
        )
      )}
    </aside>
  );
};

AiInsightsPanel.propTypes = {
  budgetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  categoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  focusItems: PropTypes.arrayOf(PropTypes.string),
  onBudgetChange: PropTypes.func,
  onHide: PropTypes.func,
  open: PropTypes.bool,
  rateCardAvailable: PropTypes.bool,
  refreshKey: PropTypes.number,
  mentionableUsers: PropTypes.array,
  requestedTab: PropTypes.oneOf(['comments', 'rate-card', 'assistant']),
};

AiInsightsPanel.defaultProps = {
  budgetId: null,
  categoryId: null,
  focusItems: [],
  onBudgetChange: () => null,
  onHide: () => null,
  open: false,
  rateCardAvailable: null,
  refreshKey: 0,
  mentionableUsers: [],
  requestedTab: null,
};

export default AiInsightsPanel;
